import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { FileSpreadsheet, Search } from "lucide-react";
import { toast } from "react-toastify";
import Select from "react-select";
import Dropdown from "../../components/inputFields/DropDown";
import DateRangePicker from "../../components/inputFields/DateRangePicker";
import ErrorBanner from "../../components/dashboard/ErrorBanner";
import { downloadProductSalesExcel, getCategories, getDomains, getProductSales } from "../../services/productSales";
import { getFyOptions, MONTH_OPTIONS, ORDER_TYPES, ORDER_STATUSES, ALL_ORDER_TYPES, PRODUCT_GROUPS, groupForSkus, filterParams } from "./productSalesOptions";

export const formatInr = (n, decimals = 0) =>
  `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: decimals })}`;
export const formatNum = (n) => Number(n).toLocaleString("en-IN");

const PAGE_SIZE = 50;
const LABEL = "text-[11px] font-semibold uppercase tracking-wider text-gray-500";
const TH = "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-600 bg-gray-100 whitespace-nowrap";
const TD = "px-3 py-2.5 text-gray-700 whitespace-nowrap border-t border-gray-100";
const FY_OPTIONS = getFyOptions();

// A row of chips where one or more can be picked (at least one stays on).
// With withAll, an "All" chip stands for every option: picking a chip while
// All is on selects just that one; clearing the last chip goes back to All.
const ChipRow = ({ label, options, selected, setSelected, hint, withAll = false }) => {
  const isAll = withAll && selected.length === options.length;
  const everything = options.map((o) => o.value);
  const toggle = (value) => {
    if (isAll) return setSelected([value]);
    const next = selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value];
    if (next.length) setSelected(next);
    else if (withAll) setSelected(everything);
  };
  const chips = withAll ? [{ value: "__all", label: "All" }, ...options] : options;
  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 px-4 py-3 sm:px-5">
      <span className={`${LABEL} mr-1`}>{label}</span>
      {chips.map((o) => {
        const on = o.value === "__all" ? isAll : !isAll && selected.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={on}
            onClick={() => (o.value === "__all" ? setSelected(everything) : toggle(o.value))}
            className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
              on ? "border-primary bg-green-100 font-semibold text-green-800" : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            {on && "✓ "}
            {o.label}
          </button>
        );
      })}
      <span className="hidden whitespace-nowrap text-xs text-gray-500 2xl:ml-auto 2xl:inline">{hint}</span>
    </div>
  );
};

// Order type + order status chips (more than one of each can be picked)
export const OrderFilters = ({ orderTypes, setOrderTypes, statuses, setStatuses }) => (
  <>
    <ChipRow
      label="Order type"
      options={ORDER_TYPES}
      selected={orderTypes}
      setSelected={setOrderTypes}
      withAll
      hint="All, or pick one or more. Default: All."
    />
    <ChipRow
      label="Order status"
      options={ORDER_STATUSES}
      selected={statuses}
      setSelected={setStatuses}
      hint="Pick one or more. Default: Delivered."
    />
  </>
);

export const NO_RANGE = { start: null, end: null };

// FY + Month, or a date range that replaces them while it is set
export const PeriodFilter = ({ fy, setFy, month, setMonth, dateRange, setDateRange }) => {
  const rangeOn = Boolean(dateRange.start && dateRange.end);
  return (
    <>
      <div className={rangeOn ? "opacity-50" : ""} title={rangeOn ? "Clear the dates to use the financial year" : undefined}>
        <p className={`${LABEL} mb-1`}>Financial year</p>
        <Dropdown options={FY_OPTIONS} value={fy} onChange={setFy} disabled={rangeOn} compact />
      </div>
      <div className={rangeOn ? "opacity-50" : ""} title={rangeOn ? "Clear the dates to use the month" : undefined}>
        <p className={`${LABEL} mb-1`}>Month</p>
        <Dropdown options={MONTH_OPTIONS} value={month} onChange={setMonth} disabled={rangeOn} compact />
      </div>
      <div>
        <div className="mb-1 flex items-center justify-between">
          <p className={LABEL}>Date range</p>
          {(dateRange.start || dateRange.end) && (
            <button type="button" onClick={() => setDateRange(NO_RANGE)} className="text-[11px] font-semibold text-green-700 hover:text-green-800">
              Clear dates
            </button>
          )}
        </div>
        <DateRangePicker compact value={dateRange} onChange={setDateRange} placeholder="Pick dates (overrides FY)" className="w-full" />
      </div>
    </>
  );
};

const METRICS = [
  { value: "qty", label: "Quantity" },
  { value: "rev", label: "Revenue" },
  { value: "asp", label: "Avg price" },
];

// Page numbers to show: a window of 5 around the current page
const pageWindow = (current, total) => {
  const start = Math.max(1, Math.min(current - 2, total - 4));
  return Array.from({ length: Math.min(5, total) }, (_, i) => start + i);
};

const ProductSales = () => {
  const [fy, setFy] = useState(FY_OPTIONS[0].value);
  const [month, setMonth] = useState("all");
  const [dateRange, setDateRange] = useState(NO_RANGE);
  const [domain, setDomain] = useState("all");
  const [domainOptions, setDomainOptions] = useState([{ value: "all", label: "All domains" }]);
  const [categories, setCategories] = useState([]); // selected Category ids, empty = all
  const [allCategories, setAllCategories] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [orderTypes, setOrderTypes] = useState(ALL_ORDER_TYPES);
  const [statuses, setStatuses] = useState(["DELIVERED"]);
  const [metric, setMetric] = useState("qty");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Ticked products (SKUs) for the combined view; kept across pages and filters.
  // ?pick=A,B comes back from the combined page's "Add or change products".
  const [picked, setPicked] = useState(() => (searchParams.get("pick") || "").split(",").filter(Boolean));
  const togglePick = (sku) => setPicked((cur) => (cur.includes(sku) ? cur.filter((s) => s !== sku) : [...cur, sku]));
  const pickedGroup = groupForSkus(picked);
  const viewCombined = () => {
    const q = new URLSearchParams({ skus: picked.join(",") });
    if (pickedGroup) q.set("group", pickedGroup.name);
    navigate(`/product-sales/combined?${q.toString()}`);
  };
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Any filter change goes back to page 1
  const withReset = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  useEffect(() => {
    getDomains()
      .then((res) => {
        const list = res.data.results || res.data;
        setDomainOptions([{ value: "all", label: "All domains" }, ...list.map((d) => ({ value: d.id, label: d.name }))]);
      })
      .catch(() => {}); // the filter still works with "All domains"
    getCategories()
      .then((res) => setAllCategories(res.data))
      .catch(() => {}); // the filter still works with "All categories"
  }, []);

  // Only the chosen domain's categories; changing domain drops categories that don't belong to it
  const categoryOptions = allCategories
    .filter((c) => domain === "all" || c.domain_id === domain)
    .map((c) => ({ value: c.id, label: c.name }));
  const changeDomain = (value) => {
    setDomain(value);
    setCategories((ids) => ids.filter((id) => value === "all" || allCategories.find((c) => c.id === id)?.domain_id === value));
    setPage(1);
  };

  // Wait until typing stops before searching
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const listParams = {
    ...filterParams({ fy, month, dateRange, orderTypes, statuses }),
    ...(domain !== "all" && { domain }),
    ...(categories.length && { categories: categories.join(",") }),
    ...(search && { search }),
    sort: metric,
  };
  const paramsKey = JSON.stringify(listParams);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    getProductSales({ ...JSON.parse(paramsKey), page, page_size: PAGE_SIZE })
      .then((res) => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [paramsKey, page]);

  const download = () => {
    setDownloading(true);
    downloadProductSalesExcel(listParams)
      .catch(() => toast.error("Could not download the Excel file."))
      .finally(() => setDownloading(false));
  };

  useEffect(() => {
    load();
  }, [load]);

  const months = data?.months || [];
  const rows = data?.results || [];
  const start = data ? (data.page - 1) * data.page_size : 0;

  const cell = (p, i) => {
    if (metric === "qty") return formatNum(p.units[i]);
    if (metric === "rev") return formatInr(p.revenue[i]);
    return p.units[i] ? formatInr(p.revenue[i] / p.units[i], 2) : "—";
  };
  const total = (p) => {
    if (metric === "qty") return formatNum(p.total_units);
    if (metric === "rev") return formatInr(p.total_revenue);
    return formatInr(p.avg_price, 2);
  };
  const statusText = ORDER_STATUSES.filter((s) => statuses.includes(s.value)).map((s) => s.label).join(" + ");
  const typeText =
    orderTypes.length === ALL_ORDER_TYPES.length
      ? "All"
      : ORDER_TYPES.filter((t) => orderTypes.includes(t.value)).map((t) => t.label).join(" + ");
  const sortText = { qty: "qty sold", rev: "revenue", asp: "avg price" }[metric];

  return (
    <div className="p-3 md:p-5 space-y-4 bg-gray-50 min-h-screen">
      {/* Header + filters */}
      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-visible">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 pt-4 sm:px-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-semibold text-gray-900 tracking-tight">Product Sales</h1>
              {loading && <span className="inline-flex h-2 w-2 animate-pulse rounded-full bg-blue-500" title="Loading" />}
            </div>
            <p className="mt-1 text-xs text-gray-500">Every number is for one product. {data?.period || `FY ${fy}`}.</p>
          </div>
          <button
            type="button"
            onClick={download}
            disabled={downloading || !data?.count}
            title="Download Excel (all products with these filters)"
            className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white shadow-md shadow-indigo-600/10 hover:bg-indigo-700 disabled:opacity-50"
          >
            <FileSpreadsheet size={18} />
            {downloading ? "Preparing…" : "Download Excel"}
          </button>
        </div>
        <div className="px-4 pb-4 pt-3 sm:px-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <PeriodFilter
              fy={fy}
              setFy={withReset(setFy)}
              month={month}
              setMonth={withReset(setMonth)}
              dateRange={dateRange}
              setDateRange={withReset(setDateRange)}
            />
            <div>
              <p className={`${LABEL} mb-1`}>Domain</p>
              <Dropdown options={domainOptions} value={domain} onChange={changeDomain} compact />
            </div>
            <div>
              <p className={`${LABEL} mb-1`}>Category</p>
              <Select
                isMulti
                options={categoryOptions}
                value={categoryOptions.filter((o) => categories.includes(o.value))}
                onChange={(selected) => withReset(setCategories)(selected.map((o) => o.value))}
                placeholder="All categories"
                classNamePrefix="react-select"
                menuPortalTarget={document.body}
                styles={{
                  control: (base) => ({ ...base, minHeight: "40px", borderRadius: "8px", borderColor: "#d1d5db" }),
                  menuPortal: (base) => ({ ...base, zIndex: 50 }),
                }}
              />
            </div>
            <label className="block">
              <span className={`${LABEL} mb-1 block`}>Search product</span>
              <span className="relative block">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Name or SKU"
                  className="h-10 w-full rounded-lg border border-gray-300 pl-9 pr-3 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-100"
                />
              </span>
            </label>
          </div>
        </div>
        <OrderFilters
          orderTypes={orderTypes}
          setOrderTypes={withReset(setOrderTypes)}
          statuses={statuses}
          setStatuses={withReset(setStatuses)}
        />
        <div className="flex flex-wrap items-center gap-2 border-t border-gray-100 px-4 py-3 sm:px-5">
          <span className={`${LABEL} mr-1`}>Quick groups</span>
          {PRODUCT_GROUPS.map((g) => {
            const on = pickedGroup?.name === g.name;
            return (
              <button
                key={g.name}
                type="button"
                aria-pressed={on}
                onClick={() => setPicked(on ? [] : g.skus)}
                className={`rounded-full border px-3 py-1.5 text-sm ${
                  on ? "border-green-500 bg-green-50 font-semibold text-green-800" : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                {on ? "✓ " : ""}
                {g.name} ({g.skus.length})
              </button>
            );
          })}
          <span className="hidden whitespace-nowrap text-xs text-gray-500 2xl:ml-auto 2xl:inline">A group ticks its products. You can also tick any products yourself.</span>
        </div>
      </div>

      {picked.length > 0 && (
        <div className="sticky top-2 z-20 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-gray-900 px-4 py-3 text-white shadow-lg">
          <p className="text-sm">
            <b>{picked.length}</b> product{picked.length === 1 ? "" : "s"} ticked{pickedGroup ? ` · ${pickedGroup.name}` : ""}
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={() => setPicked([])} className="rounded-lg border border-gray-600 px-3 py-1.5 text-sm hover:bg-gray-800">
              Clear
            </button>
            <button type="button" onClick={viewCombined} className="rounded-lg bg-green-600 px-4 py-1.5 text-sm font-bold hover:bg-green-700">
              View combined →
            </button>
          </div>
        </div>
      )}

      {error && <ErrorBanner onRetry={load} />}

      {/* Product x month table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
        <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Monthly sales by product</h2>
            <p className="mt-0.5 text-xs text-gray-500">
              Order type: {typeText} · Status: {statusText}. Click View to open a product.
            </p>
          </div>
          <div className="flex gap-1 rounded-lg bg-gray-100 p-1 self-start">
            {METRICS.map((m) => (
              <button
                key={m.value}
                type="button"
                aria-pressed={metric === m.value}
                onClick={() => withReset(setMetric)(m.value)}
                className={`rounded-md px-3.5 py-1.5 text-sm transition-colors ${
                  metric === m.value ? "bg-white font-semibold text-gray-900 shadow-sm" : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div className={`relative overflow-x-auto transition-opacity ${loading ? "opacity-50" : ""}`}>
          <table className="w-full text-sm">
            <thead>
              <tr>
                <th className={TH}>
                  <span className="sr-only">Pick</span>
                </th>
                <th className={`${TH} text-right`}>Rank</th>
                <th className={`${TH} text-left min-w-[220px]`}>Product</th>
                {months.map((m) => (
                  <th key={m} className={`${TH} text-right`}>{m}</th>
                ))}
                <th className={`${TH} text-right`}>{metric === "asp" ? "Avg" : "Total"}</th>
                <th className={TH}>
                  <span className="sr-only">Open</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p, idx) => (
                <tr key={p.product_id} className={picked.includes(p.sku) ? "bg-green-50/60" : "hover:bg-gray-50"}>
                  <td className={TD}>
                    <input
                      type="checkbox"
                      checked={picked.includes(p.sku)}
                      onChange={() => togglePick(p.sku)}
                      aria-label={`Pick ${p.name}`}
                      className="h-4 w-4 cursor-pointer accent-green-600"
                    />
                  </td>
                  <td className={`${TD} text-right font-semibold text-gray-500`}>{start + idx + 1}</td>
                  <td className={TD}>
                    <p className="font-semibold text-gray-900">{p.name}</p>
                    <p className="text-xs text-gray-500">
                      {[p.variant, p.sku, p.domain].filter(Boolean).join(" · ")}
                    </p>
                  </td>
                  {months.map((m, i) => (
                    <td key={m} className={`${TD} text-right`}>{cell(p, i)}</td>
                  ))}
                  <td className={`${TD} text-right font-bold text-gray-900 bg-gray-50`}>{total(p)}</td>
                  <td className={TD}>
                    <Link
                      to={`/product-sales/${encodeURIComponent(p.sku)}`}
                      className="text-sm font-semibold text-green-700 hover:text-green-800"
                    >
                      View
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data && rows.length === 0 && (
            <p className="py-8 text-center text-sm text-gray-500">No products sold with these filters.</p>
          )}
          {!data && loading && <p className="py-8 text-center text-sm text-gray-500">Loading…</p>}
        </div>

        {/* Pagination: 50 products per page from the API */}
        {data && data.count > 0 && (
          <div className="mt-1 flex flex-col gap-3 border-t border-gray-100 pt-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-sm text-gray-600">
              Showing {start + 1}–{start + rows.length} of {formatNum(data.count)} products · sorted by {sortText}, high to low
            </span>
            <div className="flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                disabled={data.page <= 1}
                onClick={() => setPage(data.page - 1)}
                className="h-9 rounded-lg border border-gray-300 px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                Previous
              </button>
              {pageWindow(data.page, data.total_pages).map((n) => (
                <button
                  key={n}
                  type="button"
                  aria-current={n === data.page ? "page" : undefined}
                  onClick={() => setPage(n)}
                  className={`h-9 min-w-[36px] rounded-lg border text-sm font-medium ${
                    n === data.page ? "border-primary bg-primary text-white" : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {n}
                </button>
              ))}
              <button
                type="button"
                disabled={data.page >= data.total_pages}
                onClick={() => setPage(data.page + 1)}
                className="h-9 rounded-lg border border-gray-300 px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductSales;
